/**
 * getAggregatesForSidebar.js
 * ---------------------------------------------------------------
 * Pure function. Computes national, constituency, and selected-area
 * metric aggregates for display in the sidebar.
 *
 * @returns {{ national, constituency, selected,
 *             waveSeries, waveSeriesKeys,
 *             waveSeriesKeyLabels, waveSeriesKeyColors }}
 */
import { buildLabelsAndColors } from "./helpers";
import { isMultiWaveGroup } from "../schemaTypes";
import { restrictKeysToData } from "../keyAvailability";
import {
  numericOrNull,
  weightColForGroup,
  weightColForKey,
  weightFromRowByCol,
  weightedMeanByKey,
} from "../keyWeights";

export function getAggregatesForSidebar({
  selectedFeature,
  selectedConstituencyCode,
  metricRows,
  metricRowsConstituency,
  metricRowsConstituencies,
  metricRowsUnfiltered,
  metricRowsConstituencyUnfiltered,
  group,
  groups,
  themeColours,
  waveSelect,
  preferenceFilter,
}) {
  const empty = {
    national: [],
    constituency: [],
    selected: [],
    unfilteredNational: [],
    unfilteredConstituency: [],
    unfilteredSelected: [],
    nValues: { national: null, constituency: null, selected: null },
    waveSeries: [],
    waveSeriesKeys: [],
    waveSeriesKeyLabels: {},
    waveSeriesKeyColors: {},
    keysWithData: [],
    keysWithoutData: [],
  };
  if (!group) return empty;

  const { labels } = buildLabelsAndColors(groups || {}, themeColours);

  // numericOrNull, not Number(): a question not asked in a given wave comes
  // back NULL, and Number(null) is 0 — a finite value that would be averaged in
  // as a genuine zero and pull the figure down.
  const getNumericByKey = (row, key) => {
    if (!row || !key) return null;
    const direct = numericOrNull(row?.[key]);
    if (direct !== null) return direct;
    const matchedKey = Object.keys(row).find((rk) => String(rk).includes(String(key)));
    if (!matchedKey) return null;
    return numericOrNull(row?.[matchedKey]);
  };

  const configuredKeys = (() => {
    if (Array.isArray(group?.keys) && group.keys.length) return group.keys;
    const targetVar = group?.TargetVariable1;
    if (!targetVar) return [];
    if (targetVar.type === "likert_grid") {
      const subVarIds = targetVar.subVariables || [];
      const allKeys = subVarIds.flatMap((svId) => groups?.[svId]?.keys || []);
      return Array.from(new Set(allKeys));
    }
    return targetVar.keys || [];
  })();

  const matchFn = (r, val) =>
    ["area_cd", "PCON24CD", "WD24CD", "area_id"].some(
      (f) => String(r?.[f]) === String(val)
    );

  // ── Restrict to the selected wave ──────────────────────────────────────────
  const atSelectedWave = (rows) =>
    waveSelect && isMultiWaveGroup(group)
      ? rows?.filter((g) => Number(g.Wave) === Number(waveSelect))
      : rows;

  const filteredMetricRows               = atSelectedWave(metricRows);
  const filteredmetricRowsConstituencies = atSelectedWave(metricRowsConstituencies);
  const filteredmetricRowsConstituency   = atSelectedWave(metricRowsConstituency);
  // Unfiltered ward/area rows — used for ward-level selected comparison.
  const filteredUnfiltered               = atSelectedWave(metricRowsUnfiltered);
  // Constituency-level unfiltered rows — preferred for constituency comparison
  // because ward rows use different column keys than constituency rows.
  // Falls back to filteredUnfiltered when constituency-level data not available.
  const filteredConstituencyUnfiltered = (() => {
    const src = metricRowsConstituencyUnfiltered || metricRowsUnfiltered;
    return src ? atSelectedWave(src) : null;
  })();

  // ── Options the selected wave actually carries ─────────────────────────────
  // A tracking question's option list changes between waves — Andy Burnham and
  // Rupert Lowe joined "best Prime Minister" at wave 5, Keir Starmer left it the
  // same wave — and an option that was not offered comes back NULL rather than
  // 0. Dropping those keys here removes them from every option list the sidebar
  // renders (bars, table, drill-down buttons) instead of showing a row that
  // reads 0% or a dash. The union across all row sets in scope is deliberate:
  // an option can legitimately read zero in one small area, so it only counts
  // as absent when nothing in scope has a value for it. See utils/keyAvailability.js.
  const collapseKeys = restrictKeysToData(
    [
      filteredmetricRowsConstituencies,
      filteredConstituencyUnfiltered,
      filteredUnfiltered,
      filteredmetricRowsConstituency,
      filteredMetricRows,
    ],
    configuredKeys,
  );
  const droppedKeys = configuredKeys.filter((k) => !collapseKeys.includes(k));

  /**
   * Does this row carry the question at all? True when any key in `keys` has a
   * value, which separates "asked here, nobody picked this option" from "not
   * asked here at all".
   *
   * Runs over the restricted `collapseKeys`, not the configured list: an option
   * no row in scope carries has already been dropped above, so it cannot vote
   * on whether the question is present.
   *
   * Memoised per row: the predicate is consulted once per key per row, and
   * getNumericByKey falls back to scanning every column when a key misses.
   * Without the cache a ten-key group turns one pass over 735k rows into a
   * hundred property scans each.
   */
  const rowHasAnyOf = (keys) => {
    const cache = new WeakMap();
    return (row) => {
      if (!row || typeof row !== "object") return false;
      const hit = cache.get(row);
      if (hit !== undefined) return hit;
      const has = (keys || []).some((k) => Number.isFinite(getNumericByKey(row, k)));
      cache.set(row, has);
      return has;
    };
  };

  /**
   * A likert_grid's keys span several independent questions, so one sub-variable
   * being present says nothing about whether another was asked. Every other
   * group type holds the options of a single question, where a missing option
   * among present ones is a real zero.
   */
  const isGrid = group?.type === "likert_grid"
    || group?.TargetVariable1?.type === "likert_grid";
  const hasCollapseQuestion = isGrid ? null : rowHasAnyOf(collapseKeys);

  // Roll rows up to one value per key. Each row is already a weighted % for
  // one area (and wave); combining them is a WEIGHTED mean, not a plain one —
  // a plain mean lets a small seat count as much as a large one, which under a
  // demographic filter (where per-area sample sizes diverge sharply) moves the
  // national figure by whole percentage points. Each key uses the same weight
  // column the backend divided by: n_voters for vote-share questions, n_raked
  // otherwise. See utils/keyWeights.js.
  //
  // A key with no value in a row that DOES carry the question counts as zero,
  // not as absent — see the note on groupByWave below. Skipping it instead
  // shrinks the denominator to the areas where the option exists, which is what
  // reported SNP at 27% nationally in Wave 2 (its share within Scotland).
  const collapseValueFn = hasCollapseQuestion
    ? (row, key) => {
        const v = getNumericByKey(row, key);
        if (Number.isFinite(v)) return v;
        return hasCollapseQuestion(row) ? 0 : null;
      }
    : getNumericByKey;

  const collapse = (rows) => {
    const means = weightedMeanByKey(rows, collapseKeys, { valueFn: collapseValueFn });
    return collapseKeys.map((k) => ({
      key: k,
      label: labels[k] || k,
      value: means[k],
    }));
  };
  const nForRows = (rows) => {
    const vals = (rows || [])
      .map((r) => Number(r?.n_raked ?? r?.total_sum ?? r?.weight ?? r?.n))
      .filter(Number.isFinite);
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };

  const national = filteredmetricRowsConstituencies?.length
    ? collapse(filteredmetricRowsConstituencies)
    : [];

  // Prefer the fast single-constituency fetch (metricRowsConstituency) when
  // available — it arrives within ~1s at ward level vs ~10s for the full baseline.
  // Fall back to extracting from the full baseline if not yet available.
  const constituency = (() => {
    if (!selectedConstituencyCode) return [];
    // Always filter by selectedConstituencyCode — metricRowsConstituency may contain
    // all 650 rows from an earlier overview fetch and must not be collapsed raw.
    if (filteredmetricRowsConstituency?.length) {
      const rows = filteredmetricRowsConstituency.filter((r) => matchFn(r, selectedConstituencyCode));
      if (rows.length) return collapse(rows);
    }
    if (filteredmetricRowsConstituencies?.length)
      return collapse(filteredmetricRowsConstituencies.filter((r) => matchFn(r, selectedConstituencyCode)));
    return [];
  })();

  // Don't compute "selected" from ward rows when the selected feature is a
  // constituency — ward rows match on PCON24CD giving a spurious aggregate.
  // Let "constituency" from metricRowsConstituencies be the authoritative source.
  const selected =
    selectedFeature &&
    selectedFeature.level !== "constituency" &&
    filteredMetricRows?.length
      ? collapse(filteredMetricRows.filter((r) => matchFn(r, selectedFeature.value)))
      : [];

  const unfilteredNational = filteredUnfiltered?.length
    ? collapse(filteredUnfiltered)
    : [];

  const unfilteredConstituency =
    selectedConstituencyCode && filteredConstituencyUnfiltered?.length
      ? collapse(filteredConstituencyUnfiltered.filter((r) => matchFn(r, selectedConstituencyCode)))
      : [];

  const unfilteredSelected =
    selectedFeature &&
    selectedFeature.level !== "constituency" &&
    filteredUnfiltered?.length
      ? collapse(filteredUnfiltered.filter((r) => matchFn(r, selectedFeature.value)))
      : [];
  const nationalN = filteredmetricRowsConstituencies?.length
    ? nForRows(filteredmetricRowsConstituencies)
    : null;
  const constituencyN =
    selectedConstituencyCode && filteredmetricRowsConstituencies?.length
      ? nForRows(filteredmetricRowsConstituencies.filter((r) => matchFn(r, selectedConstituencyCode)))
      : null;
  const selectedN =
    selectedFeature && filteredMetricRows?.length
      ? nForRows(filteredMetricRows.filter((r) => matchFn(r, selectedFeature.value)))
      : null;

  // ── Wave series ────────────────────────────────────────────────
  const waveSeries = [];
  let waveSeriesKeys = [];
  let waveSeriesKeyLabels = {};
  let waveSeriesKeyColors = {};

  if (isMultiWaveGroup(group)) {
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];

    // Pre-filtered geography rows (unfiltered by wave for series computation)
    const constRows = selectedConstituencyCode
      ? (metricRowsConstituencies || []).filter((r) => matchFn(r, selectedConstituencyCode))
      : [];
    const selRows = selectedFeature
      ? (metricRows || []).filter((r) => matchFn(r, selectedFeature.value))
      : [];

    // Helper: accumulate a weighted { num, den } per wave for a value function.
    // Same basis as collapse() above — the trend lines and the bars must agree,
    // so both weight by the column the value was computed over rather than
    // taking a plain mean across areas.
    //
    // `hasQuestionFn` decides what a missing value means. A nation-specific
    // party is the case that forces the distinction: an English row has no
    // figure for SNP, but the right national contribution is a real zero, not
    // an absence. Dropping those rows shrinks the denominator to Scotland and
    // reports the within-Scotland share as if it were the GB one — which is
    // how Wave 2 came to show SNP at 27% and Plaid at 11%. Waves 3+ store an
    // explicit 0 in the same cells, so the series collapsed 25pp between two
    // waves that measured the same thing. Zero-filling only where the question
    // was asked reproduces waves 3+ exactly and leaves genuinely-unasked
    // waves (Wave 1 here) absent.
    const groupByWave = (rows, valueFn, weightCol, hasQuestionFn = null) => {
      const acc = new Map();
      (rows || []).forEach((r) => {
        const w = r?.Wave;
        if (w == null) return;
        let val = valueFn(r);
        if (!Number.isFinite(val)) {
          if (!hasQuestionFn || !hasQuestionFn(r)) return;
          val = 0;
        }
        const weight = weightFromRowByCol(r, weightCol) ?? 1;
        const curr = acc.get(String(w)) || { num: 0, den: 0 };
        curr.num += weight * val;
        curr.den += weight;
        acc.set(String(w), curr);
      });
      return acc;
    };

    const mapAvg = (m, w) => {
      const rec = m.get(w);
      return rec && rec.den > 0 ? rec.num / rec.den : null;
    };

    // Collect all waves and push entries. `hasQuestionFn` is optional: pass it
    // only where a missing key means "this option scored nothing here", never
    // where it means "this question wasn't run".
    //
    // It is applied PER KEY, and only to keys some row in scope actually
    // carries. Zero-filling an option no wave ever offered would manufacture a
    // real 0% for it, which both plants a flat line on the chart and defeats
    // the restrictKeysToData pass below that exists to drop it. An option that
    // exists somewhere (SNP, in Scotland) is zero-filled; one that exists
    // nowhere is left absent.
    const buildSeries = (keysArr, valueFnFor, weightColFor, hasQuestionFn = null) => {
      const zeroFillable = hasQuestionFn
        ? new Set(restrictKeysToData([metricRowsConstituencies, constRows, selRows], keysArr))
        : null;
      const keyMaps = keysArr.map((k) => {
        const wCol = weightColFor(k);
        const hasQ = zeroFillable?.has(k) ? hasQuestionFn : null;
        return {
          k,
          nat:  groupByWave(metricRowsConstituencies, valueFnFor(k), wCol, hasQ),
          con:  groupByWave(constRows, valueFnFor(k), wCol, hasQ),
          sel:  groupByWave(selRows, valueFnFor(k), wCol, hasQ),
        };
      });

      const allWavesSet = new Set();
      keyMaps.forEach(({ nat, con, sel }) => {
        [...nat.keys(), ...con.keys(), ...sel.keys()].forEach((w) => allWavesSet.add(w));
      });
      const allWaves = Array.from(allWavesSet).sort((a, b) => Number(a) - Number(b));

      allWaves.forEach((w) => {
        const entry = { wave: Number(w) };
        keyMaps.forEach(({ k, nat, con, sel }) => {
          const selVal = mapAvg(sel, w);
          const conVal = mapAvg(con, w);
          const natVal = mapAvg(nat, w);
          entry[k] = selVal ?? conVal ?? natVal;
          // Store national and constituency separately so callers can render
          // three distinct trend lines (ward / constituency / national) when
          // a ward is selected. Desktop ignores __con; mobile TrendsChart uses it.
          entry[`${k}__nat`] = natVal;
          entry[`${k}__con`] = conVal;
        });
        waveSeries.push(entry);
      });
    };

    if (group.type === "likert_grid") {
      const subVarIds = group.subVariables || [];
      const isValidSubVar = subVarIds.includes((preferenceFilter || "").split("|")[0]);
      const isCompare = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;

      if (isCompare) {
        // One line per sub-variable showing the net (pos − neg)
        waveSeriesKeys = subVarIds;
        subVarIds.forEach((svId) => {
          waveSeriesKeyLabels[svId] = group.subLabels?.[svId] || svId;
          waveSeriesKeyColors[svId] = group.subColors?.[svId] || "#6e6e6e";
        });

        const netValueFnFor = (svId) => (row) => {
          const pol = groups?.[svId]?.polarity;
          if (!pol) return null;
          const posKeys = toArr(pol.pos);
          const negKeys = toArr(pol.neg);
          const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(getNumericByKey(row, k)));
          if (!hasData) return null;
          const pos = posKeys.reduce((acc, k) => acc + (getNumericByKey(row, k) || 0), 0);
          const neg = negKeys.reduce((acc, k) => acc + (getNumericByKey(row, k) || 0), 0);
          return pos - neg;
        };

        buildSeries(subVarIds, netValueFnFor, (svId) => weightColForGroup(groups?.[svId]));
      } else {
        // A specific sub-variable is selected — treat its keys like regular likert keys
        const [subVarId] = (preferenceFilter || "").split("|");
        const subGroup = groups?.[subVarId];
        const subKeys = subGroup?.keys || [];
        waveSeriesKeys = subKeys;
        // Derive colours from polarity membership — sub-variable groups never define
        // their own colors map, so fall back to polarity-direction semantics:
        //   pos keys → green (comfortable/trust/agree)
        //   neg keys → red  (uncomfortable/distrust/disagree)
        //   neu/dk   → grey
        const _pol = subGroup?.polarity;
        const _posSet = new Set(toArr(_pol?.pos));
        const _negSet = new Set(toArr(_pol?.neg));
        subKeys.forEach((k) => {
          waveSeriesKeyLabels[k] = subGroup.full_labels?.[k] || subGroup.labels?.[k] || k;
          waveSeriesKeyColors[k] = subGroup.colors?.[k]
            || (_posSet.has(k) ? "#15803d" : _negSet.has(k) ? "#dc2626" : "#9ca3af");
        });
        buildSeries(subKeys, (k) => (row) => {
          return getNumericByKey(row, k);
        }, (k) => weightColForKey(k), rowHasAnyOf(subKeys));
      }
    } else {
      // Regular group: one line per key
      waveSeriesKeys = group.keys || [];
      waveSeriesKeys.forEach((k) => {
        waveSeriesKeyLabels[k] = labels[k] || k;
      });
      buildSeries(waveSeriesKeys, (k) => (row) => {
        return getNumericByKey(row, k);
      }, (k) => weightColForKey(k), rowHasAnyOf(waveSeriesKeys));
    }

    // The trend spans every wave, so a key is kept when ANY wave has a value
    // for it — an option added at wave 5 still earns a line. What goes is a key
    // no wave carries at all (a question retired from the whole series, or a
    // column that never made it into the mirror), which would otherwise sit in
    // the toggle chips doing nothing.
    waveSeriesKeys = restrictKeysToData([waveSeries], waveSeriesKeys);
  }

  // ── likert_grid: override national/constituency/selected with per-sector data ─
  // The standard collapse above returns one entry per raw sentiment key (56 for
  // windfall). Compare bars need per-sector net (one bar per subVariable) in
  // compare mode, or a single sector's sentiment breakdown when a sector is
  // selected (pref = "svId" or "svId|key").
  //
  // Net formula matches Sidebar.js netFromMap exactly:
  //   net = sum(posKeys values) - sum(negKeys values)  (neutral/dk excluded)
  // Source: the collapse arrays already computed above (pre-averaged % per key).
  //
  // Categorical, plain-likert, and the waveSeries path are all untouched —
  // this block only runs when group.type === "likert_grid".
  let finalNational     = national;
  let finalConstituency = constituency;
  let finalSelected     = selected;

  if (group.type === "likert_grid") {
    const subVarIds     = group.subVariables || [];
    const [prefSvId]    = (preferenceFilter || "").split("|");
    const isValidSubVar = subVarIds.includes(prefSvId);
    const isCompare     = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;

    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];

    // Exact copy of Sidebar.js netFromMap (lines 937-947): sum pos - sum neg.
    // Neutral and dk keys are intentionally excluded to match desktop.
    const netFromMap = (byKey, polarity) => {
      if (!polarity) return null;
      const posKeys = toArr(polarity.pos);
      const negKeys = toArr(polarity.neg);
      const hasData = [...posKeys, ...negKeys].some(k => Number.isFinite(byKey[k]));
      if (!hasData) return null;
      const pos = posKeys.reduce((s, k) => s + (Number.isFinite(byKey[k]) ? byKey[k] : 0), 0);
      const neg = negKeys.reduce((s, k) => s + (Number.isFinite(byKey[k]) ? byKey[k] : 0), 0);
      return pos - neg;
    };

    // Build a flat key→value lookup from a collapse array (same shape as what
    // Sidebar.js calls toKeyMap on selectedFeatureData.national).
    const toKeyMap = (arr) =>
      Object.fromEntries((arr || []).map(({ key, value }) => [key, Number(value)]));

    if (isCompare) {
      // Compare mode: one net entry per sector, coloured by group.subColors in CompareChart.
      const natMap   = toKeyMap(national);
      const constMap = toKeyMap(constituency);
      const selMap   = toKeyMap(selected);

      const netEntries = (byKey) => subVarIds.map(svId => ({
        key:   svId,
        label: group.subLabels?.[svId] || svId,
        value: netFromMap(byKey, groups?.[svId]?.polarity),
      }));

      finalNational     = netEntries(natMap);
      finalConstituency = selectedConstituencyCode ? netEntries(constMap) : [];
      finalSelected     = selectedFeature         ? netEntries(selMap)   : [];
    } else {
      // Sector (or sector+key) selected: show that sector's sentiment breakdown.
      // Mirrors desktop: chartGroup = groupsById[subVarId], renders subGroup.keys.
      // The specific sentiment key (if present after "|") only drives map colouring;
      // Compare always shows all sentiments for the selected sector.
      const subGroup = groups?.[prefSvId];
      if (subGroup) {
        // Same wave rule as the categorical path: a sentiment the selected wave
        // did not carry is dropped rather than shown empty.
        const subKeys  = (subGroup.keys || []).filter((k) => collapseKeys.includes(k));
        const natMap   = toKeyMap(national);
        const constMap = toKeyMap(constituency);
        const selMap   = toKeyMap(selected);

        const sentimentEntries = (byKey) => subKeys.map(k => ({
          key:   k,
          label: subGroup.full_labels?.[k] || subGroup.labels?.[k] || k,
          value: Number.isFinite(byKey[k]) ? byKey[k] : null,
        }));

        finalNational     = sentimentEntries(natMap);
        finalConstituency = selectedConstituencyCode ? sentimentEntries(constMap) : [];
        finalSelected     = selectedFeature         ? sentimentEntries(selMap)   : [];
      }
    }
  }

  return {
    national:     finalNational,
    constituency: finalConstituency,
    selected:     finalSelected,
    unfilteredNational,
    unfilteredConstituency,
    unfilteredSelected,
    nValues: { national: nationalN, constituency: constituencyN, selected: selectedN },
    waveSeries,
    waveSeriesKeys,
    waveSeriesKeyLabels,
    waveSeriesKeyColors,
    // Which response options the selected wave carries, for the option pickers
    // that render from the group config rather than from these aggregates.
    keysWithData: collapseKeys,
    keysWithoutData: droppedKeys,
  };
}
